<script setup lang="ts">
// This file and supporting utilities allow "animations" to be included with a CMS text string
// without requiring rich text or additional CMS configuration fields

import { parseAllDataBoundaries, useTypewriterEffect } from '@/animationUtilities'
import { onUnmounted, ref } from 'vue'

const props = defineProps({
  text: {
    type: String,
    default: '',
  },
})

const unmountFunctions: Array<() => void> = []

// Parse props.text for commands and data
const parsed = parseAllDataBoundaries(props.text ?? '')

// Create corresponding refs for each command's animated output
const animatedRefs = parsed.commands.map(() => ref(''))

/** Set up a controller for each command we support, if required
 *
 * Detailed usage and example described in `animationUtilities.ts`
 *
 * Notes:
 * cmd.command is the control word
 * cmd.data is an array of values to display (or work with)
 * the 'element' command is meant as a way to insert HTML e.g. '{{element:break}}' will insert a <br /> element.
 * the 'typewriter' command is made to animate a typewriter effect using the words in data. (This requires a controller)
 */
parsed.commands.forEach((cmd, index) => {
  const animatedRef = animatedRefs[index]!

  // Add 'controllers' for commands where necessary
  switch (cmd.command) {
    case 'typewriter':
      const te = useTypewriterEffect(cmd.data, animatedRef, {
        charDelay: 200,
        stringPauseDelay: 2000,
        loopRestartDelay: 2000,
      })
      unmountFunctions.push(te.stop)
      te.animate()
      break
  }
})

// Animate the 'cursor' (only show for typewriter commands)
// The typed word is placed in 'animatedRef[x]' but the animated cursor at the end also needs its own ref()
const cursorAnimation = ref('|')
const hasTypewriter = parsed.commands.some((c) => c.command === 'typewriter')

if (hasTypewriter) {
  const cursorInterval = setInterval(() => {
    cursorAnimation.value = cursorAnimation.value === '▏' ? ' ' : '▏'
  }, 600)
  unmountFunctions.push(() => clearInterval(cursorInterval))
}

onUnmounted(() => {
  unmountFunctions.forEach((fn) => fn())
})
</script>

<template>
  <div>
    <p class="inline-block">
      <!-- Text segments are inteleaved with animation commands / data -->
      <template v-for="(segment, i) in parsed.textSegments" :key="i">
        <span class="text-yellow">{{ segment }}</span>
        <template v-if="i < parsed.commands.length">
          <!-- Allow display of commands[i].data -->
          <span class="text-white">{{ animatedRefs[i] }}</span>
          <span
            v-if="parsed.commands[i]!.command === 'typewriter'"
            class="text-gray-400 font-extralight"
          >
            {{ cursorAnimation }}
          </span>
          <br
            v-if="
              parsed.commands[i]!.command === 'element' &&
              parsed.commands[i]!.data.includes('break')
            "
          />
        </template>
      </template>
    </p>
  </div>
</template>

<style scoped></style>
